import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import DeleteButton from '../editors/DeleteButton';

interface Project {
  id: number;
  titleAr: string;
  titleEn: string;
  categoryAr: string;
  categoryEn: string;
  locationAr: string;
  locationEn: string;
  date: string;
  descriptionAr: string;
  descriptionEn: string;
  image: string;
  areaAr: string;
  areaEn: string;
  durationAr: string;
  durationEn: string;
}

interface ProjectsGridSectionProps {
  language?: 'ar' | 'en';
  projects?: Project[];
  onDeleteProject?: (index: number) => void;
}

const ProjectsGridSection: React.FC<ProjectsGridSectionProps> = ({
  language = 'ar',
  projects = [
    {
      id: 1,
      titleAr: 'برج الأعمال التجارية',
      titleEn: 'Commercial Business Tower',
      categoryAr: 'المباني',
      categoryEn: 'Buildings',
      locationAr: 'القاهرة، مصر',
      locationEn: 'Cairo, Egypt',
      date: '2023',
      descriptionAr: 'برج تجاري حديث بارتفاع 30 طابق مع أحدث التقنيات والتصميم المعماري المتميز',
      descriptionEn: 'Modern commercial tower with 30 floors featuring the latest technologies and distinguished architectural design',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      areaAr: '50,000 متر مربع',
      areaEn: '50,000 sqm',
      durationAr: '24 شهر',
      durationEn: '24 months'
    },
    {
      id: 2,
      titleAr: 'طريق الملك فهد السريع',
      titleEn: 'King Fahd Highway',
      categoryAr: 'البنية التحتية',
      categoryEn: 'Infrastructure',
      locationAr: 'الإسكندرية، مصر',
      locationEn: 'Alexandria, Egypt',
      date: '2022',
      descriptionAr: 'طريق سريع بطول 50 كم مع جسور وأنفاق متطورة',
      descriptionEn: 'Highway with a length of 50 km with advanced bridges and tunnels',
      image: 'https://images.unsplash.com/photo-1545454675-3531b543be5d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      areaAr: '50 كم',
      areaEn: '50 km',
      durationAr: '18 شهر',
      durationEn: '18 months'
    },
    {
      id: 3,
      titleAr: 'مجمع سكني فاخر',
      titleEn: 'Luxury Residential Complex',
      categoryAr: 'السكنية',
      categoryEn: 'Residential',
      locationAr: 'القاهرة الجديدة، مصر',
      locationEn: 'New Cairo, Egypt',
      date: '2023',
      descriptionAr: 'مجمع سكني فاخر يحتوي على 200 وحدة سكنية مع مرافق ترفيهية متكاملة',
      descriptionEn: 'Luxury residential complex containing 200 housing units with integrated recreational facilities',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      areaAr: '25,000 متر مربع',
      areaEn: '25,000 sqm',
      durationAr: '30 شهر',
      durationEn: '30 months'
    }
  ],
  onDeleteProject
}) => {
  const isRTL = language === 'ar';

  return (
    <section className="section-padding">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              viewport={{ once: true }}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {onDeleteProject && (
                <DeleteButton onDelete={() => onDeleteProject(index)} />
              )}
              
              {/* Project Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={isRTL ? project.titleAr : project.titleEn}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-gray-800">
                    {isRTL ? project.categoryAr : project.categoryEn}
                  </span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {isRTL ? project.titleAr : project.titleEn}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {isRTL ? project.descriptionAr : project.descriptionEn}
                </p>

                {/* Project Details */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-gray-500">
                    <FaMapMarkerAlt className="w-4 h-4" />
                    <span>{isRTL ? project.locationAr : project.locationEn}</span>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-gray-500">
                    <FaCalendarAlt className="w-4 h-4" />
                    <span>{project.date}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    <span className="font-semibold">{isRTL ? 'المساحة:' : 'Area:'}</span> {isRTL ? project.areaAr : project.areaEn}
                  </div>
                  <div className="text-sm text-gray-500">
                    <span className="font-semibold">{isRTL ? 'المدة:' : 'Duration:'}</span> {isRTL ? project.durationAr : project.durationEn}
                  </div>
                </div>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary w-full"
                >
                  {isRTL ? 'عرض التفاصيل' : 'View Details'}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsGridSection; 